import {
  RepurchaseProductsSummary,
  RepurchaseProductsTable,
  RepurchaseSummaryCardGrid,
} from "@fero/components";
import {
  useDocumentDownloaderProps,
  useEmailPanelControls,
  useGetPhysicalAddressQuery,
  useSettlementCurrency,
} from "@fero/hooks";
import {
  membershipData,
  PortalPageContent,
  PortalPageContentHeader,
} from "@fero/layouts";
import AwcOrderAttachmentWorker from "@fero/workers/awcOrderAttachment?worker";
import { Stack, Typography } from "@mui/material";
import type { SaleOrderDetailsView } from "adl-gen/ferovinum/app/api";
import type {
  DeliveryOption,
  DeliveryStatus,
  SaleOrderId,
  SaleOrderStatus,
  SaleOrderStatusEvent,
} from "adl-gen/ferovinum/app/db";
import type { AttachedDocument } from "adl-gen/ferovinum/app/uploads";
import { assertNever } from "assert-never";
import { DeliveryUpdatesCard } from "components/library/widgets/card/delivery-updates-card";
import { StatusStepper } from "components/library/widgets/status-stepper";
import { TabbedPage } from "components/library/widgets/tabbed-page";
import { LoadingActionButtonWithConfirmation } from "components/widgets/buttons/loading-action-button/loading-action-button";
import {
  EmailSourceReferenceType,
  EmailsPanel,
} from "components/widgets/email/emails";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { adlMapToMap } from "utils/adl-utils";
import { formatInstant, formatLocalDate } from "utils/date-utils";
import { DocumentDownloader } from "../../../../../../../../packages/components/widgets/buttons/document-downloader";

export interface OrganisationRepurchaseDetailsPageViewProps {
  adminView: boolean;
  saleOrderDetailsView: SaleOrderDetailsView;
  organisationName: string;

  onConfirmClientPaid(invoiceNumber: string): Promise<void>;

  onAdminCancel(invoiceNumber: string): Promise<void>;

  onAdminRelease(invoiceNumber: string): Promise<void>;

  pdfDownloadActions: {
    attachedDocuments: AttachedDocument[];
  };
}

export const OrganisationRepurchaseDetailsPageView = ({
  pdfDownloadActions,
  adminView,
  saleOrderDetailsView,
  organisationName,
  ...adminActions
}: OrganisationRepurchaseDetailsPageViewProps) => {
  const {
    orderStatusEvents,
    saleDate,
    invoiceNumber,
    usesSettlementCredit,
    deliveryOption,
    deliveryStatuses,
    requiresDeliveryOrder,
  } = saleOrderDetailsView;
  const latestStatusEvent = useMemo(
    () => orderStatusEvents[orderStatusEvents.length - 1],
    [orderStatusEvents],
  );
  const { activeStep, steps } = useMemo(
    () =>
      getSteps({
        events: orderStatusEvents,
        saleDate,
        organisationName,
        paidWithCredit: usesSettlementCredit,
        requiresDeliveryOrder,
      }),
    [
      orderStatusEvents,
      organisationName,
      requiresDeliveryOrder,
      saleDate,
      usesSettlementCredit,
    ],
  );

  const { showEmailPanel, authToken } = useEmailPanelControls();

  const { saleOrderId } = useParams<{ saleOrderId: SaleOrderId }>();

  const tabs = [
    {
      label: "Progress",
      key: "Progress",
      toolbar: (
        <ToolbarButtons
          saleOrderDetailsView={saleOrderDetailsView}
          adminView={adminView}
          {...pdfDownloadActions}
        />
      ),
      content: (
        <OrganisationRepurchaseProgressSection
          deliveryOption={deliveryOption}
          deliveryStatuses={deliveryStatuses}
          activeStep={activeStep}
          steps={steps}
        />
      ),
    },
    {
      label: "Order Summary",
      key: "Order Summary",
      toolbar: (
        <ToolbarButtons
          saleOrderDetailsView={saleOrderDetailsView}
          adminView={adminView}
          {...pdfDownloadActions}
        />
      ),
      content: (
        <OrganisationRepurchaseOrderSummarySection
          saleOrderDetailsView={saleOrderDetailsView}
        />
      ),
    },
  ];

  if (showEmailPanel) {
    tabs.push({
      label: "Emails",
      key: "Emails",
      toolbar: (
        <ToolbarButtons
          saleOrderDetailsView={saleOrderDetailsView}
          adminView={adminView}
          {...pdfDownloadActions}
        />
      ),
      content: (
        <EmailsPanel
          {...{
            referenceType: EmailSourceReferenceType.SaleOrder,
            reference: saleOrderId,
            authToken: authToken,
          }}
        />
      ),
    });
  }

  return (
    <PortalPageContent
      header={
        <OrganisationRepurchaseDetailsHeader invoiceNumber={invoiceNumber} />
      }
    >
      <Stack spacing={4}>
        <TabbedPage tabs={tabs} />
        {adminView && (
          <ActionButtons
            invoiceNumber={invoiceNumber}
            status={latestStatusEvent?.status}
            usesSettlementCredit={usesSettlementCredit}
            requiresDeliveryOrder={requiresDeliveryOrder}
            {...adminActions}
          />
        )}
      </Stack>
    </PortalPageContent>
  );
};

const ToolbarButtons = ({
  attachedDocuments,
  saleOrderDetailsView,
  adminView,
}: {
  attachedDocuments: AttachedDocument[];
  saleOrderDetailsView: SaleOrderDetailsView;
  adminView: boolean;
}) => {
  const pdfDownloaderProps = useDocumentDownloaderProps(attachedDocuments);
  const org =
    saleOrderDetailsView.dealLegSaleOrders[0].productWithId.value.ownerId;
  const { data: orgAddress } = useGetPhysicalAddressQuery(
    org,
    "businessAddress",
  );
  return (
    <Stack direction="row" spacing={2}>
      <DocumentDownloader
        {...pdfDownloaderProps}
        generateAWCOrderAttachment={
          saleOrderDetailsView.repurchaseStorageLocation?.warehouseGroup ===
            "awc" && adminView
            ? () => {
                return new Promise((resolve) => {
                  const awcOrderAttachment = new AwcOrderAttachmentWorker();
                  awcOrderAttachment.onmessage = (e: MessageEvent) => {
                    if (e.data.ready === "xlsx") {
                      const a = document.createElement("a");
                      a.href =
                        "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64," +
                        e.data.data;
                      a.download = `awc_order_attachment_${saleOrderDetailsView.invoiceNumber}.xlsx`;
                      a.click();
                      resolve();
                    } else if (e.data.error) {
                      resolve();
                      throw e.data.error;
                    }
                  };

                  awcOrderAttachment.postMessage({
                    saleOrderDetailsView,
                    organisation: membershipData.organisations.find(
                      (o) => o.id === org,
                    ),
                    address: orgAddress?.address,
                  });
                });
              }
            : undefined
        }
      />
    </Stack>
  );
};

const OrganisationRepurchaseDetailsHeader = ({
  invoiceNumber,
}: {
  invoiceNumber: string;
}) => {
  const title = "Sale Order";
  return (
    <PortalPageContentHeader
      variant="split"
      title={title}
      right={
        <Stack width={"100%"} textAlign={"right"}>
          <Typography sx={{ fontSize: 12, color: "common.grey5" }}>
            Reference Number
          </Typography>
          <Typography variant="subtitle1Bold">{invoiceNumber}</Typography>
        </Stack>
      }
    />
  );
};

const OrganisationRepurchaseProgressSection = ({
  deliveryOption,
  deliveryStatuses,
  activeStep,
  steps,
}: {
  deliveryOption: DeliveryOption | null;
  deliveryStatuses: DeliveryStatus[];
  activeStep: number;
  steps: StatusStep[];
}) => {
  return (
    <Stack spacing={5}>
      {(deliveryOption?.kind === "shippedDelivery" ||
        deliveryOption?.kind === "orgLocationDelivery") && (
        <DeliveryUpdatesCard deliveryStatuses={deliveryStatuses} />
      )}
      <StatusStepper activeStep={activeStep} steps={steps} sx={{ flex: 1 }} />
    </Stack>
  );
};

const OrganisationRepurchaseOrderSummarySection = ({
  saleOrderDetailsView,
}: {
  saleOrderDetailsView: SaleOrderDetailsView;
}) => {
  const settlementCurrency = useSettlementCurrency();
  const attributeSelection = adlMapToMap(
    saleOrderDetailsView.attributeSelection,
  );
  return (
    <Stack spacing={5}>
      <RepurchaseSummaryCardGrid
        {...saleOrderDetailsView}
        repurchaseStorageLocationName={
          saleOrderDetailsView.repurchaseStorageLocation?.storageLocationName ??
          null
        }
        destinationStorageLocationName={
          saleOrderDetailsView.destinationStorageLocation
            ?.storageLocationName ?? null
        }
      />
      <RepurchaseProductsTable
        {...saleOrderDetailsView.saleOrderTotalsView}
        settlementCurrency={settlementCurrency}
        dealLegSaleOrders={saleOrderDetailsView.dealLegSaleOrders}
        currency={settlementCurrency}
        attributeSelection={attributeSelection}
      />
      <RepurchaseProductsSummary
        {...saleOrderDetailsView.saleOrderTotalsView}
        {...saleOrderDetailsView}
        settlementCurrency={settlementCurrency}
        usingCredit={saleOrderDetailsView.usesSettlementCredit}
      />
    </Stack>
  );
};

function checkCanMarkAsCancelled(
  usesSettlementCredit: boolean,
  status?: SaleOrderStatus,
) {
  return (
    !usesSettlementCredit &&
    (status === "invoiced" || status === "draftInvoiced")
  );
}

function checkCanMarkAsPaid(
  usesSettlementCredit: boolean,
  status?: SaleOrderStatus,
) {
  return usesSettlementCredit ? status === "released" : status === "invoiced";
}

function checkCanMarkAsReleased(
  usesSettlementCredit: boolean,
  requiresDeliveryOrder: boolean,
  status?: SaleOrderStatus,
) {
  const releaseStatus = requiresDeliveryOrder
    ? "deliveryOrderSigned"
    : usesSettlementCredit
      ? "invoiced"
      : "paid";
  return status === releaseStatus;
}

const ActionButtons = ({
  invoiceNumber,
  onConfirmClientPaid,
  onAdminCancel,
  onAdminRelease,
  status,
  usesSettlementCredit,
  requiresDeliveryOrder,
}: Pick<
  OrganisationRepurchaseDetailsPageViewProps,
  "onConfirmClientPaid" | "onAdminCancel" | "onAdminRelease"
> & {
  invoiceNumber: string;
  status?: SaleOrderStatus;
  usesSettlementCredit: boolean;
  requiresDeliveryOrder: boolean;
}) => {
  const canMarkAsCancelled = checkCanMarkAsCancelled(
    usesSettlementCredit,
    status,
  );
  const canMarkAsPaid = checkCanMarkAsPaid(usesSettlementCredit, status);
  const canMarkAsReleased = checkCanMarkAsReleased(
    usesSettlementCredit,
    requiresDeliveryOrder,
    status,
  );
  return (
    <Stack
      direction="row"
      spacing={2}
      paddingBottom={5}
      justifyContent="flex-end"
    >
      {canMarkAsCancelled && (
        <LoadingActionButtonWithConfirmation
          variant="outlined"
          confirmationTitle="Please confirm that you want to CANCEL this Sale Order"
          onConfirm={async () => onAdminCancel(invoiceNumber)}
        >
          Cancel Sale Order
        </LoadingActionButtonWithConfirmation>
      )}
      {canMarkAsPaid && (
        <LoadingActionButtonWithConfirmation
          confirmationTitle="Please confirm that you want to CONFIRM the client has paid"
          onConfirm={async () => await onConfirmClientPaid(invoiceNumber)}
        >
          Confirm Client Paid
        </LoadingActionButtonWithConfirmation>
      )}
      {canMarkAsReleased && (
        <LoadingActionButtonWithConfirmation
          confirmationTitle="Please confirm that you want to RELEASE this Sale Order"
          onConfirm={async () => await onAdminRelease(invoiceNumber)}
        >
          Release Sale Order
        </LoadingActionButtonWithConfirmation>
      )}
    </Stack>
  );
};

type SaleOrderViewStatus = SaleOrderStatus | "requested";

interface StatusStep {
  status: SaleOrderViewStatus;
  label: string;
  timestamp?: string;
}

interface StatusSteps {
  activeStep: number;
  steps: StatusStep[];
}

const getSteps = ({
  organisationName,
  events,
  saleDate,
  paidWithCredit,
  requiresDeliveryOrder,
}: {
  organisationName: string;
  events: SaleOrderStatusEvent[];
  saleDate: string;
  paidWithCredit?: boolean;
  requiresDeliveryOrder: boolean;
}): StatusSteps => {
  let activeStep = 0;
  const sortedEvents = [...events].sort((x, y) => x.createdAt - y.createdAt);

  let steps: StatusStep[] = sortedEvents.map((e) => {
    return {
      status: e.status,
      label: getLabelForStatus(e.status, organisationName),
      timestamp: formatInstant(e.createdAt),
    };
  });

  steps = [
    {
      status: "requested",
      label: `Requested by ${organisationName}`,
      timestamp: formatLocalDate(saleDate),
    },
    ...steps,
  ];
  activeStep = steps.length;

  const lastStepStatus = steps[activeStep - 1].status;
  if (lastStepStatus === "canceled") {
    return { activeStep, steps };
  }

  const statusSteps: StatusStep[] = getStatusSteps(
    requiresDeliveryOrder,
    organisationName,
    paidWithCredit,
  );

  /// Add follow-up steps
  const stepIndex = statusSteps.findIndex((s) => s.status === lastStepStatus);
  const missingDefaultSteps: StatusStep[] = statusSteps.slice(stepIndex + 1);
  steps = [...steps, ...missingDefaultSteps];

  return { activeStep, steps };
};

function getLabelForStatus(
  status: SaleOrderViewStatus,
  organisationName?: string,
) {
  switch (status) {
    case "canceled":
      return "Cancelled";
    case "draftInvoiced":
      return "Preparing invoice";
    case "invoiced":
      return "Invoice sent";
    case "paid":
      return "Payment received by Ferovinum";
    case "deliveryOrderRequested":
      return "Delivery order requested";
    case "deliveryOrderSigned":
      return "Delivery order signed";
    case "released":
      return `Released stock to ${organisationName}`;
    case "requested":
      return `Requested by ${organisationName}`;
    default:
      // @ts-ignore
      assertNever(status);
  }
}

const statusMapping: Record<string, SaleOrderViewStatus[]> = {
  creditDeliveryOrder: [
    "draftInvoiced",
    "invoiced",
    "released",
    "deliveryOrderRequested",
    "deliveryOrderSigned",
    "paid",
  ],
  creditNoDeliveryOrder: ["draftInvoiced", "invoiced", "released", "paid"],
  noCreditDeliveryOrder: [
    "draftInvoiced",
    "invoiced",
    "paid",
    "deliveryOrderRequested",
    "deliveryOrderSigned",
    "released",
  ],
  noCreditNoDeliveryOrder: ["draftInvoiced", "invoiced", "paid", "released"],
};

function getStatusSteps(
  requiresDeliveryOrder: boolean,
  organisationName: string,
  paidWithCredit?: boolean,
): StatusStep[] {
  const key = `${paidWithCredit ? "credit" : "noCredit"}${requiresDeliveryOrder ? "DeliveryOrder" : "NoDeliveryOrder"}`;
  const statuses = statusMapping[key];

  return statuses.map((status) => {
    return { status, label: getLabelForStatus(status, organisationName) };
  });
}
